import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, Plus, Pencil, Trash2, ArrowLeft, ExternalLink, Sparkles, Code2, PenLine, RefreshCw, Upload, X, ImagePlus } from "lucide-react";
import { toast } from "sonner";

const CATS = ["Liquid Cultures", "Substrates", "Grow Bags", "Guides", "Cultivation Tips", "News"];
const empty = { title: "", slug: "", category: "Guides", featured_image_url: "", excerpt: "", content: "", meta_title: "", meta_description: "", status: "draft" };
const slugify = (t) => (t || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");

// --- content <img> helpers (work in both Rich-text and Paste-HTML modes) ---
const parseImages = (html) => {
  try {
    const doc = new DOMParser().parseFromString(`<div id="__r">${html || ""}</div>`, "text/html");
    return [...doc.querySelectorAll("#__r img")].map((img) => ({ src: img.getAttribute("src") || "", alt: img.getAttribute("alt") || "" }));
  } catch { return []; }
};
const withImages = (html, mutate) => {
  try {
    const doc = new DOMParser().parseFromString(`<div id="__r">${html || ""}</div>`, "text/html");
    mutate([...doc.querySelectorAll("#__r img")]);
    return doc.querySelector("#__r").innerHTML;
  } catch { return html; }
};

// --- "Post Images" manager row (own prompt state so typing never re-renders siblings) ---
function ImageRow({ index, img, onRegenerate, onRemove }) {
  const [prompt, setPrompt] = useState(img.alt || "");
  const [busy, setBusy] = useState(false);
  const regen = async () => {
    if (!prompt.trim()) { toast.error("Describe the image first"); return; }
    setBusy(true);
    try { await onRegenerate(index, prompt.trim()); } finally { setBusy(false); }
  };
  return (
    <div className="flex gap-3 p-3 border border-border rounded-sm bg-background/40" data-testid={`image-row-${index}`}>
      <img src={img.src} alt="" className="w-20 h-20 object-cover rounded-sm border border-border shrink-0" />
      <div className="flex-1 min-w-0 flex flex-col gap-2">
        <Input value={prompt} onChange={(e) => setPrompt(e.target.value)} data-testid={`image-prompt-${index}`}
          placeholder="Describe this image to regenerate it" className="text-xs h-8" />
        <div className="flex items-center gap-2">
          <Button type="button" size="sm" variant="outline" onClick={regen} disabled={busy} data-testid={`regen-image-${index}`} className="rounded-full h-7 text-[11px]">
            {busy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <RefreshCw className="w-3 h-3 mr-1" />} Regenerate
          </Button>
          <Button type="button" size="sm" variant="ghost" onClick={() => onRemove(index)} data-testid={`remove-image-${index}`} className="rounded-full h-7 text-[11px] text-muted-foreground hover:text-destructive">
            <X className="w-3 h-3 mr-1" /> Remove
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function AdminBlog() {
  const { user, ready } = useAuth();
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const [slugTouched, setSlugTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [aiTopic, setAiTopic] = useState("");
  const [drafting, setDrafting] = useState(false);
  const [htmlMode, setHtmlMode] = useState(false);
  const [genImages, setGenImages] = useState(true);
  const [imageCount, setImageCount] = useState(2);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const quillRef = useRef(null);
  const lastRangeRef = useRef(null);
  const htmlRef = useRef(null);
  const uploadRef = useRef(() => {});

  const uploadFile = async (file) => {
    if (!file || !file.type?.startsWith("image/")) { toast.error("Please choose an image file"); return null; }
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    return data.url;
  };

  const insertImageAtCursor = (url, alt = "") => {
    if (htmlMode) {
      const tag = `<figure><img src="${url}" alt="${alt}"></figure>`;
      const ta = htmlRef.current;
      setForm((f) => {
        const c = f.content || "";
        const pos = ta ? ta.selectionStart : c.length;
        return { ...f, content: c.slice(0, pos) + tag + c.slice(pos) };
      });
    } else {
      const editor = quillRef.current?.getEditor?.();
      if (!editor) { setForm((f) => ({ ...f, content: (f.content || "") + `<img src="${url}">` })); return; }
      const index = (lastRangeRef.current && lastRangeRef.current.index) ?? editor.getLength();
      editor.insertEmbed(index, "image", url, "user");
      editor.setSelection(index + 1, 0);
    }
  };

  const handleUploadFiles = async (files) => {
    const list = [...(files || [])].filter((f) => f.type?.startsWith("image/"));
    if (!list.length) return;
    setUploading(true);
    try {
      for (const file of list) {
        const url = await uploadFile(file);
        if (url) insertImageAtCursor(url, "");
      }
      toast.success(list.length > 1 ? `${list.length} images added` : "Image added");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Upload failed");
    } finally { setUploading(false); }
  };
  uploadRef.current = handleUploadFiles;

  const regenImage = async (index, prompt) => {
    try {
      const { data } = await api.post("/admin/generate-image", { prompt }, { timeout: 120000 });
      setForm((f) => ({ ...f, content: withImages(f.content, (imgs) => {
        if (imgs[index]) { imgs[index].setAttribute("src", data.url); imgs[index].setAttribute("alt", prompt); }
      }) }));
      toast.success("Image regenerated");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't regenerate image");
    }
  };

  const removeImage = (index) => {
    setForm((f) => ({ ...f, content: withImages(f.content, (imgs) => {
      const im = imgs[index];
      if (im) { const fig = im.closest("figure"); (fig || im).remove(); }
    }) }));
  };

  const quillModules = useMemo(() => ({
    toolbar: {
      container: [
        [{ header: [2, 3, false] }],
        ["bold", "italic", "underline", "blockquote"],
        [{ list: "ordered" }, { list: "bullet" }],
        ["link", "image"],
        ["clean"],
      ],
      handlers: {
        image: () => {
          const input = document.createElement("input");
          input.type = "file";
          input.accept = "image/*";
          input.onchange = () => uploadRef.current(input.files);
          input.click();
        },
      },
    },
  }), []);

  const postImages = useMemo(() => parseImages(form.content), [form.content]);

  useEffect(() => {
    if (ready && (!user || user.role !== "admin")) navigate("/login");
  }, [ready, user, navigate]);

  const load = () => api.get("/admin/posts").then((r) => setPosts(r.data.posts || [])).catch(() => {});
  useEffect(() => { if (user?.role === "admin") load(); }, [user]);

  const effectiveSlug = useMemo(() => (slugTouched ? form.slug : slugify(form.title)), [form.title, form.slug, slugTouched]);

  const startNew = () => { setForm(empty); setEditingId(null); setSlugTouched(false); setShowForm(true); setAiTopic(""); setHtmlMode(false); setImageCount(2); };

  const draftWithAI = async () => {
    if (!aiTopic.trim()) { toast.error("Enter a topic first"); return; }
    setDrafting(true);
    try {
      const { data } = await api.post("/admin/posts/ai-draft",
        { topic: aiTopic.trim(), category: form.category, generate_images: genImages, image_count: imageCount },
        { timeout: 240000 });
      setForm((f) => ({
        ...f,
        title: data.title || f.title,
        excerpt: data.excerpt || f.excerpt,
        content: data.content || f.content,
        featured_image_url: data.featured_image_url || f.featured_image_url,
        meta_title: data.meta_title || f.meta_title,
        meta_description: data.meta_description || f.meta_description,
      }));
      setSlugTouched(false);
      setHtmlMode(false);
      toast.success(genImages ? "Draft + images generated — review before publishing" : "Draft generated — review before publishing");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "AI draft failed");
    } finally { setDrafting(false); }
  };

  const startEdit = async (p) => {
    const { data } = await api.get(`/admin/posts/${p.id}`);
    setForm({ title: data.title, slug: data.slug, category: data.category, featured_image_url: data.featured_image_url || "",
      excerpt: data.excerpt || "", content: data.content || "", meta_title: data.meta_title || "",
      meta_description: data.meta_description || "", status: data.status });
    setEditingId(p.id); setSlugTouched(true); setShowForm(true);
    window.scrollTo(0, 0);
  };

  const save = async (statusOverride) => {
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    setSaving(true);
    const payload = { ...form, slug: effectiveSlug, status: statusOverride || form.status };
    try {
      if (editingId) await api.put(`/admin/posts/${editingId}`, payload);
      else await api.post("/admin/posts", payload);
      toast.success(payload.status === "published" ? "Published live to /blog" : "Draft saved");
      setShowForm(false); setForm(empty); setEditingId(null); load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  const del = async (p) => {
    if (!window.confirm(`Delete "${p.title}"?`)) return;
    try { await api.delete(`/admin/posts/${p.id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Delete failed"); }
  };

  return (
    <div className="min-h-screen pt-28 pb-24 max-w-5xl mx-auto px-5 sm:px-8">
      <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
        <div>
          <button onClick={() => navigate("/admin")} className="inline-flex items-center gap-1.5 font-mono-label text-[11px] text-muted-foreground hover:text-primary transition-colors mb-2">
            <ArrowLeft className="w-3.5 h-3.5" /> Admin
          </button>
          <h1 className="font-display text-4xl font-light">Blog Publishing</h1>
        </div>
        {!showForm && (
          <Button onClick={startNew} data-testid="new-post-btn" className="rounded-full">
            <Plus className="w-4 h-4 mr-1" /> New Post
          </Button>
        )}
      </div>

      {showForm ? (
        <div className="border border-border bg-card/40 rounded-sm p-6 sm:p-8 space-y-5" data-testid="post-form">
          <div className="rounded-sm border border-primary/30 bg-primary/[0.06] p-4" data-testid="ai-draft-panel">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="w-4 h-4 text-primary" />
              <Label className="font-mono-label text-[11px] text-primary">Draft with AI</Label>
            </div>
            <p className="text-[11px] text-muted-foreground mb-3">Enter a topic and let Claude write a full first draft — title, body, excerpt & SEO. You can edit everything before publishing.</p>
            <div className="flex flex-col sm:flex-row gap-2">
              <Input
                value={aiTopic}
                onChange={(e) => setAiTopic(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !drafting) { e.preventDefault(); draftWithAI(); } }}
                data-testid="ai-topic-input"
                placeholder="e.g. How to make agar plates for cloning"
                className="flex-1"
              />
              <Button onClick={draftWithAI} disabled={drafting} data-testid="ai-draft-btn" className="rounded-full shrink-0">
                {drafting ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> {genImages ? "Drafting + images…" : "Drafting…"}</> : <><Sparkles className="w-4 h-4 mr-1.5" /> Draft</>}
              </Button>
            </div>
            <label className="flex items-center gap-2 mt-3 cursor-pointer w-fit" data-testid="ai-images-toggle">
              <Checkbox checked={genImages} onCheckedChange={(v) => setGenImages(!!v)} disabled={drafting} data-testid="ai-images-checkbox" />
              <span className="text-[11px] text-muted-foreground">Generate images (a featured image + inline photos). Adds ~20-60s.</span>
            </label>
            {genImages && (
              <div className="flex items-center gap-2 mt-2.5" data-testid="ai-image-count">
                <span className="text-[11px] text-muted-foreground">Inline photos in the article:</span>
                <Select value={String(imageCount)} onValueChange={(v) => setImageCount(Number(v))} disabled={drafting}>
                  <SelectTrigger className="h-7 w-16 text-xs" data-testid="ai-image-count-trigger"><SelectValue /></SelectTrigger>
                  <SelectContent>{[1, 2, 3, 4].map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
          </div>
          <div>
            <Label className="font-mono-label text-[11px]">Title</Label>
            <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="post-title" className="mt-1" placeholder="How to make a clean liquid culture" />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label className="font-mono-label text-[11px]">Slug (URL)</Label>
              <Input value={effectiveSlug} onChange={(e) => { setSlugTouched(true); setForm({ ...form, slug: e.target.value }); }} data-testid="post-slug" className="mt-1 font-mono text-xs" />
              <p className="text-[10px] text-muted-foreground mt-1">/blog/{effectiveSlug || "…"}</p>
            </div>
            <div>
              <Label className="font-mono-label text-[11px]">Category</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger className="mt-1" data-testid="post-category"><SelectValue /></SelectTrigger>
                <SelectContent>{CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="font-mono-label text-[11px]">Featured Image URL</Label>
            <Input value={form.featured_image_url} onChange={(e) => setForm({ ...form, featured_image_url: e.target.value })} data-testid="post-image" className="mt-1" placeholder="https://…" />
          </div>
          <div>
            <Label className="font-mono-label text-[11px]">Excerpt</Label>
            <Textarea value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} data-testid="post-excerpt" className="mt-1" rows={2} placeholder="Short summary shown on the blog feed" />
          </div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <Label className="font-mono-label text-[11px]">Content</Label>
              <div className="inline-flex rounded-full border border-border overflow-hidden" data-testid="content-mode-toggle">
                <button type="button" onClick={() => setHtmlMode(false)} data-testid="mode-richtext"
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 font-mono-label text-[10px] transition-colors ${!htmlMode ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-primary"}`}>
                  <PenLine className="w-3 h-3" /> Rich text
                </button>
                <button type="button" onClick={() => setHtmlMode(true)} data-testid="mode-html"
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 font-mono-label text-[10px] transition-colors ${htmlMode ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-primary"}`}>
                  <Code2 className="w-3 h-3" /> Paste HTML
                </button>
              </div>
            </div>
            {htmlMode ? (
              <div data-testid="post-content-html">
                <Textarea
                  ref={htmlRef}
                  value={form.content}
                  onChange={(e) => setForm((f) => ({ ...f, content: e.target.value }))}
                  data-testid="post-html-source"
                  rows={16}
                  className="font-mono text-xs leading-relaxed"
                  placeholder="Paste your full HTML here (from Marblism, a doc, anywhere). Image tags, headings and links are kept exactly as-is."
                />
                <p className="text-[10px] text-muted-foreground mt-1.5">
                  Paste raw HTML and it's saved untouched — <span className="text-primary">&lt;img&gt;</span> tags with public https:// URLs render on the live post. Switch to Rich text to visually tweak.
                </p>
              </div>
            ) : (
              <div className="mt-1 quill-dark" data-testid="post-content">
                <ReactQuill ref={quillRef} theme="snow" value={form.content}
                  onChange={(v) => setForm((f) => ({ ...f, content: v }))}
                  onChangeSelection={(range) => { if (range) lastRangeRef.current = range; }}
                  modules={quillModules} />
              </div>
            )}
          </div>
          <div className="border-t border-border pt-4" data-testid="post-images-panel">
            <div className="flex items-center gap-2 mb-3">
              <ImagePlus className="w-4 h-4 text-primary" />
              <Label className="font-mono-label text-[11px]">Post Images</Label>
              <span className="text-[10px] text-muted-foreground">{postImages.length} in article</span>
            </div>
            <div
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => { e.preventDefault(); setDragOver(false); handleUploadFiles(e.dataTransfer.files); }}
              className={`rounded-sm border border-dashed p-5 text-center transition-colors cursor-pointer ${dragOver ? "border-primary bg-primary/10" : "border-border hover:border-primary/60"}`}
              onClick={() => document.getElementById("blog-upload-input")?.click()}
              data-testid="image-upload-zone"
            >
              <input id="blog-upload-input" type="file" accept="image/*" multiple hidden
                onChange={(e) => { handleUploadFiles(e.target.files); e.target.value = ""; }} data-testid="image-upload-input" />
              {uploading ? (
                <span className="inline-flex items-center gap-2 text-xs text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin" /> Uploading…</span>
              ) : (
                <span className="inline-flex items-center gap-2 text-xs text-muted-foreground"><Upload className="w-4 h-4" /> Drag & drop photos here, or click to upload — inserted at your cursor</span>
              )}
            </div>
            {postImages.length > 0 && (
              <div className="grid sm:grid-cols-2 gap-3 mt-4">
                {postImages.map((img, i) => (
                  <ImageRow key={`${img.src}-${i}`} index={i} img={img} onRegenerate={regenImage} onRemove={removeImage} />
                ))}
              </div>
            )}
          </div>
          <div className="grid sm:grid-cols-2 gap-4 pt-2 border-t border-border">
            <div>
              <Label className="font-mono-label text-[11px]">Meta Title (SEO)</Label>
              <Input value={form.meta_title} onChange={(e) => setForm({ ...form, meta_title: e.target.value })} data-testid="post-meta-title" className="mt-1" placeholder="Defaults to post title" />
            </div>
            <div>
              <Label className="font-mono-label text-[11px]">Meta Description (SEO)</Label>
              <Input value={form.meta_description} onChange={(e) => setForm({ ...form, meta_description: e.target.value })} data-testid="post-meta-desc" className="mt-1" placeholder="Defaults to excerpt" />
            </div>
          </div>
          <div className="flex items-center gap-3 pt-2 flex-wrap">
            <Button onClick={() => save("published")} disabled={saving} data-testid="publish-btn" className="rounded-full">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Publish"}
            </Button>
            <Button onClick={() => save("draft")} disabled={saving} variant="outline" data-testid="save-draft-btn" className="rounded-full">
              Save Draft
            </Button>
            <Button onClick={() => { setShowForm(false); setForm(empty); setEditingId(null); }} variant="ghost" className="rounded-full">Cancel</Button>
          </div>
        </div>
      ) : (
        <div className="border border-border divide-y divide-border rounded-sm overflow-hidden" data-testid="posts-list">
          {posts.length === 0 ? (
            <p className="p-8 text-center text-muted-foreground text-sm">No posts yet. Click "New Post" to write your first article.</p>
          ) : posts.map((p) => (
            <div key={p.id} className="flex items-center gap-4 p-4 hover:bg-card/40 transition-colors" data-testid={`post-row-${p.slug}`}>
              <div className="flex-1 min-w-0">
                <p className="text-sm truncate">{p.title}</p>
                <p className="font-mono-label text-[10px] text-muted-foreground mt-1">
                  {p.category} · <span className={p.status === "published" ? "text-primary" : "text-amber-500"}>{p.status.toUpperCase()}</span>
                </p>
              </div>
              {p.status === "published" && (
                <a href={`/blog/${p.slug}`} target="_blank" rel="noreferrer" className="text-muted-foreground hover:text-primary" title="View live"><ExternalLink className="w-4 h-4" /></a>
              )}
              <button onClick={() => startEdit(p)} data-testid={`edit-${p.slug}`} className="text-muted-foreground hover:text-primary"><Pencil className="w-4 h-4" /></button>
              <button onClick={() => del(p)} data-testid={`delete-${p.slug}`} className="text-muted-foreground hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
